import produce from 'immer';

import {RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';

export type FileType = 'csv' | 'sdf';

export type ModalStep = 'CONFIGURE' | 'FILE_UPLOAD' | 'MAP_COLUMNS' | 'SUBMITTING' | 'DONE';

// TODO(BNCH-153068): Extract to shared location (e.g. client/registry/shared/) when building the reusable RegistrationConfigPane.
export interface RegistrationConfig {
  registryId: string;
  namingStrategy: RegistryLabelingStrategy;
  folderId: string;
}

export interface ModalState {
  step: ModalStep;
  initialFolderId: string | null;
  schemaId: string | null;
  compoundSchemaId: string | null;
  registrationConfig: RegistrationConfig | null;
  file: File | null;
  s3Url: string | null;
  headers: Array<string> | null;
  columnMappings: Record<string, string>;
  fieldExpansion: Record<string, Array<string>>;
}

export const INITIAL_STATE: ModalState = {
  step: 'CONFIGURE',
  initialFolderId: null,
  schemaId: null,
  compoundSchemaId: null,
  registrationConfig: null,
  file: null,
  s3Url: null,
  headers: null,
  columnMappings: {},
  fieldExpansion: {},
};

interface SetConfigurationAction {
  type: 'SET_CONFIGURATION';
  payload: {
    schemaId: string | null;
    compoundSchemaId: string | null;
    registrationConfig: RegistrationConfig | null;
  };
}

interface SetFileUploadResultAction {
  type: 'SET_FILE_UPLOAD_RESULT';
  payload: {
    file: File;
    s3Url: string;
    headers: Array<string>;
  };
}

interface ClearFileUploadAction {
  type: 'CLEAR_FILE_UPLOAD';
}

interface SetColumnMappingsAction {
  type: 'SET_COLUMN_MAPPINGS';
  payload: {
    columnMappings: Record<string, string>;
    fieldExpansion: Record<string, Array<string>>;
  };
}

interface GoToStepAction {
  type: 'GO_TO_STEP';
  payload: {
    step: ModalStep;
  };
}

interface ResetAction {
  type: 'RESET';
}

export type ModalAction =
  | SetConfigurationAction
  | SetFileUploadResultAction
  | ClearFileUploadAction
  | SetColumnMappingsAction
  | GoToStepAction
  | ResetAction;

export default function moleculeImportReducer(state: ModalState, action: ModalAction): ModalState {
  switch (action.type) {
    case 'SET_CONFIGURATION':
      return produce(state, (draft) => {
        draft.schemaId = action.payload.schemaId;
        draft.compoundSchemaId = action.payload.compoundSchemaId;
        draft.registrationConfig = action.payload.registrationConfig;
      });
    case 'SET_FILE_UPLOAD_RESULT':
      return produce(state, (draft) => {
        draft.file = action.payload.file;
        draft.s3Url = action.payload.s3Url;
        draft.headers = action.payload.headers;
      });
    case 'CLEAR_FILE_UPLOAD':
      return produce(state, (draft) => {
        draft.file = null;
        draft.s3Url = null;
        draft.headers = null;
      });
    case 'SET_COLUMN_MAPPINGS':
      return produce(state, (draft) => {
        draft.columnMappings = action.payload.columnMappings;
        draft.fieldExpansion = action.payload.fieldExpansion;
      });
    case 'GO_TO_STEP': {
      const stepOrder: Array<ModalStep> = ['CONFIGURE', 'FILE_UPLOAD', 'MAP_COLUMNS', 'SUBMITTING', 'DONE'];
      const targetIndex = stepOrder.indexOf(action.payload.step);
      const currentIndex = stepOrder.indexOf(state.step);
      return produce(state, (draft) => {
        draft.step = action.payload.step;
        if (targetIndex < currentIndex) {
          const fileUploadIndex = stepOrder.indexOf('FILE_UPLOAD');
          if (targetIndex < fileUploadIndex) {
            draft.file = null;
            draft.s3Url = null;
            draft.headers = null;
          }
          const mapColumnsIndex = stepOrder.indexOf('MAP_COLUMNS');
          if (targetIndex < mapColumnsIndex) {
            draft.columnMappings = {};
            draft.fieldExpansion = {};
          }
        }
      });
    }
    case 'RESET':
      return INITIAL_STATE;
    default:
      return state;
  }
}
